import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom'
import { AuthProvider, useAuth } from './context/AuthContext'
import { RequirePermission } from './components/RequirePermission'
import { RequireProductionAccess } from './components/RequireProductionAccess'
import { RequireProductionRequestsAccess } from './components/RequireProductionRequestsAccess'
import { RequireCategoriesAccess } from './components/RequireCategoriesAccess'
import { RequirePlanningAccess } from './components/RequirePlanningAccess'
import { RequireProductionScheduleAccess } from './components/RequireProductionScheduleAccess'
import { RequireInventoryAccess } from './components/RequireInventoryAccess'
import { PERMISSION_KEYS } from './config/permissions'
import { AppLayout } from './layouts/AppLayout'
import { AdminLayout } from './layouts/AdminLayout'
import { HomePage } from './pages/HomePage'
import { LoginPage } from './pages/LoginPage'
import { AdministrationLayout } from './pages/admin/AdministrationLayout'
import { AdminHomePage } from './pages/admin/AdminHomePage'
import { EmployeesPage } from './pages/admin/employees/EmployeesPage'
import { EmployeeFormPage } from './pages/admin/employees/EmployeeFormPage'
import { RolesPage } from './pages/admin/roles/RolesPage'
import { RoleFormPage } from './pages/admin/roles/RoleFormPage'
import { ProductionLayout } from './pages/admin/production/ProductionLayout'
import { ProductionRequestsLayout } from './pages/admin/production/ProductionRequestsLayout'
import { ProductsListPage } from './pages/admin/production/ProductsListPage'
import { ProductFormPage } from './pages/admin/production/ProductFormPage'
import { ConsumablesListPage } from './pages/admin/production/ConsumablesListPage'
import { ConsumableFormPage } from './pages/admin/production/ConsumableFormPage'
import { ProductionRequestsListPage } from './pages/admin/production/ProductionRequestsListPage'
import { ProductionRequestFormPage } from './pages/admin/production/ProductionRequestFormPage'
import { CategoriesLayout, CategoriesIndexRedirect } from './pages/admin/categories/CategoriesLayout'
import { CategoriesAdminPage } from './pages/admin/categories/CategoriesAdminPage'
import { MachinesPage } from './pages/admin/machines/MachinesPage'
import { MachineFormPage } from './pages/admin/machines/MachineFormPage'
import { ShiftsPage } from './pages/admin/shifts/ShiftsPage'
import { ShiftFormPage } from './pages/admin/shifts/ShiftFormPage'
import { ProductionWorkersPage } from './pages/admin/production-workers/ProductionWorkersPage'
import { ProductionWorkerFormPage } from './pages/admin/production-workers/ProductionWorkerFormPage'
import { ProductionCalendarPage } from './pages/admin/calendar/ProductionCalendarPage'
import { PlanningLayout } from './pages/planning/PlanningLayout'
import { ScheduleGanttPage } from './pages/planning/ScheduleGanttPage'
import { PlanningQueuePage } from './pages/planning/PlanningQueuePage'
import { OutputReportPage } from './pages/planning/OutputReportPage'
import { TimesheetsPage } from './pages/planning/TimesheetsPage'
import { MaterialsPage } from './pages/planning/MaterialsPage'
import { KioskPage } from './pages/planning/KioskPage'
import { KpiPage } from './pages/planning/KpiPage'
import { OeePage } from './pages/planning/OeePage'
import { CostsPage } from './pages/planning/CostsPage'
import { FulfillmentPage } from './pages/planning/FulfillmentPage'
import { TraceabilityPage } from './pages/planning/TraceabilityPage'
import { InventoryPage } from './pages/inventory/InventoryPage'
import { ShipmentsPage } from './pages/shipments/ShipmentsPage'
import { ShipmentFormPage } from './pages/shipments/ShipmentFormPage'
import { NotificationsPage } from './pages/notifications/NotificationsPage'

function LegacyAdminEmployeesIdRedirect() {
  const { id } = useParams<{ id: string }>()
  return <Navigate to={`/admin/administration/employees/${id}`} replace />
}

function LegacyAdminRolesIdRedirect() {
  const { id } = useParams<{ id: string }>()
  return <Navigate to={`/admin/administration/roles/${id}`} replace />
}

/** Старые ссылки `/admin/production/...` → `/production/...` */
function RedirectAdminProductionToRoot() {
  const { pathname } = useLocation()
  const suffix = pathname.replace(/^\/admin\/production\/?/, '')
  const to = suffix === '' ? '/production' : `/production/${suffix}`
  return <Navigate to={to} replace />
}

function ProductionDefaultRedirect() {
  const { can } = useAuth()
  if (can(PERMISSION_KEYS.PRODUCTS_READ)) {
    return <Navigate to="products" replace />
  }
  if (can(PERMISSION_KEYS.CONSUMABLES_READ)) {
    return <Navigate to="consumables" replace />
  }
  return <Navigate to="/" replace />
}

function PlanningDefaultRedirect() {
  const { can } = useAuth()
  if (can(PERMISSION_KEYS.PRODUCTION_SCHEDULE_READ)) {
    return <Navigate to="schedule" replace />
  }
  if (can(PERMISSION_KEYS.PRODUCTION_REQUESTS_READ)) {
    return <Navigate to="queue" replace />
  }
  if (can(PERMISSION_KEYS.TIMESHEETS_READ)) {
    return <Navigate to="timesheets" replace />
  }
  if (can(PERMISSION_KEYS.MATERIALS_READ)) {
    return <Navigate to="materials" replace />
  }
  if (can(PERMISSION_KEYS.PLANNING_KIOSK)) {
    return <Navigate to="kiosk" replace />
  }
  return <Navigate to="/" replace />
}

/** Старые URL `/production/requests` → отдельный раздел `/production-requests`. */
function LegacyProductionRequestsRedirect() {
  const { pathname } = useLocation()
  const rest = pathname.replace(/^\/production\/requests\/?/, '')
  const to = rest ? `/production-requests/${rest}` : '/production-requests'
  return <Navigate to={to} replace />
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route element={<AppLayout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/production/requests/*" element={<LegacyProductionRequestsRedirect />} />
            <Route
              path="/production-requests"
              element={
                <RequireProductionRequestsAccess>
                  <ProductionRequestsLayout />
                </RequireProductionRequestsAccess>
              }
            >
              <Route
                index
                element={
                  <RequirePermission permission={PERMISSION_KEYS.PRODUCTION_REQUESTS_READ}>
                    <ProductionRequestsListPage />
                  </RequirePermission>
                }
              />
              <Route
                path="new"
                element={
                  <RequirePermission permission={PERMISSION_KEYS.PRODUCTION_REQUESTS_WRITE}>
                    <ProductionRequestFormPage />
                  </RequirePermission>
                }
              />
              <Route
                path=":id"
                element={
                  <RequirePermission permission={PERMISSION_KEYS.PRODUCTION_REQUESTS_READ}>
                    <ProductionRequestFormPage />
                  </RequirePermission>
                }
              />
            </Route>
            <Route
              path="/planning"
              element={
                <RequirePlanningAccess>
                  <PlanningLayout />
                </RequirePlanningAccess>
              }
            >
              <Route index element={<PlanningDefaultRedirect />} />
              <Route
                path="schedule"
                element={
                  <RequireProductionScheduleAccess>
                    <ScheduleGanttPage />
                  </RequireProductionScheduleAccess>
                }
              />
              <Route
                path="queue"
                element={
                  <RequirePermission permission={PERMISSION_KEYS.PRODUCTION_REQUESTS_READ}>
                    <PlanningQueuePage />
                  </RequirePermission>
                }
              />
              <Route
                path="output"
                element={
                  <RequireProductionScheduleAccess>
                    <OutputReportPage />
                  </RequireProductionScheduleAccess>
                }
              />
              <Route
                path="timesheets"
                element={
                  <RequirePermission permission={PERMISSION_KEYS.TIMESHEETS_READ}>
                    <TimesheetsPage />
                  </RequirePermission>
                }
              />
              <Route
                path="materials"
                element={
                  <RequirePermission permission={PERMISSION_KEYS.MATERIALS_READ}>
                    <MaterialsPage />
                  </RequirePermission>
                }
              />
              <Route
                path="kiosk"
                element={
                  <RequirePermission permission={PERMISSION_KEYS.PLANNING_KIOSK}>
                    <KioskPage />
                  </RequirePermission>
                }
              />
              <Route
                path="kpi"
                element={
                  <RequireProductionScheduleAccess>
                    <KpiPage />
                  </RequireProductionScheduleAccess>
                }
              />
              <Route
                path="oee"
                element={
                  <RequireProductionScheduleAccess>
                    <OeePage />
                  </RequireProductionScheduleAccess>
                }
              />
              <Route
                path="costs"
                element={
                  <RequireProductionScheduleAccess>
                    <CostsPage />
                  </RequireProductionScheduleAccess>
                }
              />
              <Route
                path="fulfillment"
                element={
                  <RequirePermission permission={PERMISSION_KEYS.PRODUCTION_REQUESTS_READ}>
                    <FulfillmentPage />
                  </RequirePermission>
                }
              />
              <Route
                path="traceability"
                element={
                  <RequireProductionScheduleAccess>
                    <TraceabilityPage />
                  </RequireProductionScheduleAccess>
                }
              />
            </Route>
            <Route
              path="/inventory"
              element={
                <RequireInventoryAccess>
                  <InventoryPage />
                </RequireInventoryAccess>
              }
            />
            <Route
              path="/shipments"
              element={
                <RequirePermission permission={PERMISSION_KEYS.SHIPMENTS_READ}>
                  <ShipmentsPage />
                </RequirePermission>
              }
            />
            <Route
              path="/shipments/new"
              element={
                <RequirePermission permission={PERMISSION_KEYS.SHIPMENTS_WRITE}>
                  <ShipmentFormPage />
                </RequirePermission>
              }
            />
            <Route
              path="/shipments/:id"
              element={
                <RequirePermission permission={PERMISSION_KEYS.SHIPMENTS_READ}>
                  <ShipmentFormPage />
                </RequirePermission>
              }
            />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route
              path="/production"
              element={
                <RequireProductionAccess>
                  <ProductionLayout />
                </RequireProductionAccess>
              }
            >
              <Route index element={<ProductionDefaultRedirect />} />
              <Route
                path="products"
                element={
                  <RequirePermission permission={PERMISSION_KEYS.PRODUCTS_READ}>
                    <ProductsListPage />
                  </RequirePermission>
                }
              />
              <Route
                path="products/new"
                element={
                  <RequirePermission permission={PERMISSION_KEYS.PRODUCTS_WRITE}>
                    <ProductFormPage />
                  </RequirePermission>
                }
              />
              <Route
                path="products/:id"
                element={
                  <RequirePermission permission={PERMISSION_KEYS.PRODUCTS_WRITE}>
                    <ProductFormPage />
                  </RequirePermission>
                }
              />
              <Route
                path="consumables"
                element={
                  <RequirePermission permission={PERMISSION_KEYS.CONSUMABLES_READ}>
                    <ConsumablesListPage />
                  </RequirePermission>
                }
              />
              <Route
                path="consumables/new"
                element={
                  <RequirePermission permission={PERMISSION_KEYS.CONSUMABLES_WRITE}>
                    <ConsumableFormPage />
                  </RequirePermission>
                }
              />
              <Route
                path="consumables/:id"
                element={
                  <RequirePermission permission={PERMISSION_KEYS.CONSUMABLES_WRITE}>
                    <ConsumableFormPage />
                  </RequirePermission>
                }
              />
            </Route>
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<Navigate to="administration/panel" replace />} />
              <Route path="administration" element={<AdministrationLayout />}>
                <Route index element={<Navigate to="panel" replace />} />
                <Route path="panel" element={<AdminHomePage />} />
                <Route
                  path="employees"
                  element={
                    <RequirePermission permission={PERMISSION_KEYS.USERS_READ}>
                      <EmployeesPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="employees/new"
                  element={
                    <RequirePermission permission={PERMISSION_KEYS.USERS_WRITE}>
                      <EmployeeFormPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="employees/:id"
                  element={
                    <RequirePermission permission={PERMISSION_KEYS.USERS_WRITE}>
                      <EmployeeFormPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="roles"
                  element={
                    <RequirePermission permission={PERMISSION_KEYS.ROLES_READ}>
                      <RolesPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="roles/new"
                  element={
                    <RequirePermission permission={PERMISSION_KEYS.ROLES_WRITE}>
                      <RoleFormPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="roles/:id"
                  element={
                    <RequirePermission permission={PERMISSION_KEYS.ROLES_WRITE}>
                      <RoleFormPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="categories"
                  element={
                    <RequireCategoriesAccess>
                      <CategoriesLayout />
                    </RequireCategoriesAccess>
                  }
                >
                  <Route index element={<CategoriesIndexRedirect />} />
                  <Route
                    path="consumables"
                    element={
                      <RequirePermission permission={PERMISSION_KEYS.CONSUMABLES_READ}>
                        <CategoriesAdminPage kind="consumables" />
                      </RequirePermission>
                    }
                  />
                  <Route
                    path="products"
                    element={
                      <RequirePermission permission={PERMISSION_KEYS.PRODUCTS_READ}>
                        <CategoriesAdminPage kind="products" />
                      </RequirePermission>
                    }
                  />
                </Route>
                <Route
                  path="machines"
                  element={
                    <RequirePermission permission={PERMISSION_KEYS.MACHINES_READ}>
                      <MachinesPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="machines/new"
                  element={
                    <RequirePermission permission={PERMISSION_KEYS.MACHINES_WRITE}>
                      <MachineFormPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="machines/:id"
                  element={
                    <RequirePermission permission={PERMISSION_KEYS.MACHINES_WRITE}>
                      <MachineFormPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="shifts"
                  element={
                    <RequirePermission permission={PERMISSION_KEYS.SHIFTS_READ}>
                      <ShiftsPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="shifts/new"
                  element={
                    <RequirePermission permission={PERMISSION_KEYS.SHIFTS_WRITE}>
                      <ShiftFormPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="shifts/:id"
                  element={
                    <RequirePermission permission={PERMISSION_KEYS.SHIFTS_WRITE}>
                      <ShiftFormPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="production-workers"
                  element={
                    <RequirePermission permission={PERMISSION_KEYS.PRODUCTION_WORKERS_READ}>
                      <ProductionWorkersPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="production-workers/new"
                  element={
                    <RequirePermission permission={PERMISSION_KEYS.PRODUCTION_WORKERS_WRITE}>
                      <ProductionWorkerFormPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="production-workers/:id"
                  element={
                    <RequirePermission permission={PERMISSION_KEYS.PRODUCTION_WORKERS_WRITE}>
                      <ProductionWorkerFormPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="calendar"
                  element={
                    <RequirePermission permission={PERMISSION_KEYS.PRODUCTION_CALENDAR_READ}>
                      <ProductionCalendarPage />
                    </RequirePermission>
                  }
                />
              </Route>
              <Route path="employees/new" element={<Navigate to="/admin/administration/employees/new" replace />} />
              <Route path="employees/:id" element={<LegacyAdminEmployeesIdRedirect />} />
              <Route path="employees" element={<Navigate to="/admin/administration/employees" replace />} />
              <Route path="roles/new" element={<Navigate to="/admin/administration/roles/new" replace />} />
              <Route path="roles/:id" element={<LegacyAdminRolesIdRedirect />} />
              <Route path="roles" element={<Navigate to="/admin/administration/roles" replace />} />
              <Route
                path="categories/consumables"
                element={<Navigate to="/admin/administration/categories/consumables" replace />}
              />
              <Route
                path="categories/products"
                element={<Navigate to="/admin/administration/categories/products" replace />}
              />
              <Route path="categories" element={<Navigate to="/admin/administration/categories" replace />} />
              <Route path="production/*" element={<RedirectAdminProductionToRoot />} />
            </Route>
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  )
}
